import classNames from "classnames";
import { ScrollArea } from "components/ScrollArea/ScrollArea";
import SignIn from "components/SignIn";
import OutOfCredits from "features/Subscription/components/OutOfCredits";
import Table from "components/Table";
import useLabels from "hooks/useLabels";
import React from "react";
import { CreditedFeature } from "store/slices/subscription";
import { useConsensusSnapshot } from "../hooks/useConsensusSnapshot";
import { ConsensusMeterData } from "../types";
import {
  categoryAggregationsToIcons,
  orderedCategoryAggregations,
  orderedMeterCategories,
} from "../utils";
import { CategoryResultsRow } from "./CategoryResultRow";
import { SnapshotToggleButton } from "./SnapshotToggleButton";

interface ResultsTableProps {
  consensusMeterResults: ConsensusMeterData | undefined;
  consensusMeterSuccess: boolean;
  isFilteringEnabled: boolean;
  onToggleSnapshot: () => void;
  threadInteractionId?: string;
  searchId?: string;
}

const OVERLAY_CLASSNAME = "relative z-10 -mt-40 sm:-mt-[100px] -ml-[3px] ";

export const ResultsTable = ({
  consensusMeterResults,
  consensusMeterSuccess,
  isFilteringEnabled,
  onToggleSnapshot,
  threadInteractionId,
  searchId,
}: ResultsTableProps) => {
  const [aggregationLabels, tableLabels, scoresDescription] = useLabels(
    "consensus-meter.aggregations",
    "consensus-meter.table",
    "consensus-meter.scores-description"
  );
  const { isOutOfCredits, isNotSignedIn } = useConsensusSnapshot(searchId);

  // Use Fragment for restricted users, ScrollArea for full access users
  const TableWrapper =
    isNotSignedIn || isOutOfCredits ? React.Fragment : ScrollArea;
  const wrapperProps =
    isNotSignedIn || isOutOfCredits
      ? {}
      : { orientation: "horizontal" as const };

  return (
    <div>
      <div
        className={classNames(
          "w-full",
          isNotSignedIn || isOutOfCredits ? "overflow-hidden max-h-[300px]" : ""
        )}
        data-testid="meter-results-table"
      >
        <TableWrapper {...wrapperProps}>
          <Table className="leading-[normal]">
            <Table.THead>
              <Table.TR>
                <Table.TH className="min-w-[300px]">
                  {tableLabels["results"]}
                </Table.TH>
                {orderedCategoryAggregations.map((aggregation) => (
                  <Table.TH key={aggregation} className="w-[114px]">
                    <div className="flex gap-3 items-center">
                      <span>{aggregationLabels[aggregation]}</span>
                      <i
                        className={classNames(
                          categoryAggregationsToIcons[aggregation],
                          "text-fg-muted bold-icon"
                        )}
                      />
                    </div>
                  </Table.TH>
                ))}
              </Table.TR>
            </Table.THead>
            <Table.TBody>
              {orderedMeterCategories.map((category) => (
                <CategoryResultsRow
                  category={category}
                  key={category}
                  categoryData={consensusMeterResults?.[category] ?? null}
                  numResultsAnalyzed={
                    consensusMeterResults?.num_results_analyzed ?? 0
                  }
                  categoryWins={
                    consensusMeterResults?.categoryToWins[category] ?? []
                  }
                  aggregationTies={consensusMeterResults?.aggregationTies ?? []}
                  consensusMeterSuccess={consensusMeterSuccess}
                  isFilteringEnabled={isFilteringEnabled}
                  threadInteractionId={threadInteractionId}
                  searchId={searchId}
                />
              ))}
            </Table.TBody>
          </Table>
        </TableWrapper>
      </div>

      {!isOutOfCredits && !isNotSignedIn && (
        <div
          className={
            "flex justify-end flex-1 items-center w-full @md:relative @md:bottom-[29px] @md:h-0 @md:py-0 py-6 border-b border-border-base @md:border-b-0"
          }
        >
          <SnapshotToggleButton
            isSnapshotOpen={true}
            onToggleSnapshot={onToggleSnapshot}
            buttonClassName="w-full @md:w-auto"
          />
        </div>
      )}
      {isOutOfCredits && (
        <div className={OVERLAY_CLASSNAME}>
          <OutOfCredits
            feature={CreditedFeature.CONSENSUS_SNAPSHOT}
            bottomRightComponent={
              <SnapshotToggleButton
                isSnapshotOpen={true}
                onToggleSnapshot={onToggleSnapshot}
              />
            }
            containerClassName="ai-card-paywall"
          />
        </div>
      )}
      {isNotSignedIn && (
        <div className={OVERLAY_CLASSNAME}>
          <SignIn
            feature={CreditedFeature.CONSENSUS_SNAPSHOT}
            bottomRightComponent={
              <SnapshotToggleButton
                isSnapshotOpen={true}
                onToggleSnapshot={onToggleSnapshot}
              />
            }
            containerClassName="ai-card-paywall"
          />
        </div>
      )}
    </div>
  );
};
